import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CalendarDays, Clock, MapPin, UserCheck, Loader2, Save,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  bg: "#ffffff",
  border: "#e2e6ef",
  borderMuted: "#eef1f5",
  title: "#062546",
  body: "#5a6577",
  subtle: "#8896a7",
  primary: "var(--primary-color, #4A90D9)",
};

export default function EventEditModal({ open, onClose, event, onSaved }) {
  const [form, setForm] = useState({
    name: "", date: "", start_time: "", end_time: "", location: "", instructor: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setForm({
        name: event.name || "",
        date: event.date || "",
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        location: event.location || "",
        instructor: event.instructor || "",
      });
    }
  }, [event]);

  const handleSave = async () => {
    if (!event) return;
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    if (!form.start_time) { toast.error("Start time is required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/events/${event.id}`,
        { ...form, name: form.name.trim() },
        { withCredentials: true },
      );
      toast.success("Event updated");
      if (onSaved) onSaved(data);
      if (onClose) onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose && onClose(); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] p-0 flex flex-col overflow-hidden"
        style={{ backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 16 }}
        data-testid="event-edit-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0" style={{ borderBottom: `1px solid ${COLORS.borderMuted}` }}>
          <DialogTitle className="font-heading font-bold text-xl flex items-center gap-2" style={{ color: COLORS.title }}>
            <CalendarDays className="h-4 w-4" style={{ color: COLORS.primary }} />
            Edit event
          </DialogTitle>
          <DialogDescription className="font-body text-xs" style={{ color: COLORS.subtle }}>
            Update event details. Check-ins and the public URL stay the same.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="font-body text-xs uppercase tracking-wider" style={{ color: COLORS.subtle }}>
              Name
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Session name"
              data-testid="event-edit-name-input"
              className="h-10 text-sm font-body"
              style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-body text-xs uppercase tracking-wider flex items-center gap-1.5" style={{ color: COLORS.subtle }}>
              <CalendarDays className="h-3 w-3" /> Date
            </Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              data-testid="event-edit-date-input"
              className="h-10 text-sm font-body"
              style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-body text-xs uppercase tracking-wider flex items-center gap-1.5" style={{ color: COLORS.subtle }}>
                <Clock className="h-3 w-3" /> Start
              </Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                data-testid="event-edit-start-input"
                className="h-10 text-sm font-body"
                style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-body text-xs uppercase tracking-wider" style={{ color: COLORS.subtle }}>
                End (optional)
              </Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                data-testid="event-edit-end-input"
                className="h-10 text-sm font-body"
                style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-body text-xs uppercase tracking-wider flex items-center gap-1.5" style={{ color: COLORS.subtle }}>
              <MapPin className="h-3 w-3" /> Location
            </Label>
            <Input
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="e.g. Simulation Center — Room 101"
              data-testid="event-edit-location-input"
              className="h-10 text-sm font-body"
              style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-body text-xs uppercase tracking-wider flex items-center gap-1.5" style={{ color: COLORS.subtle }}>
              <UserCheck className="h-3 w-3" /> Instructor
            </Label>
            <Input
              value={form.instructor}
              onChange={(e) => setForm((f) => ({ ...f, instructor: e.target.value }))}
              placeholder="Instructor name"
              data-testid="event-edit-instructor-input"
              className="h-10 text-sm font-body"
              style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
            />
          </div>
        </div>

        <div className="flex justify-end items-center gap-2 px-6 py-3 shrink-0" style={{ borderTop: `1px solid ${COLORS.borderMuted}`, backgroundColor: COLORS.bg }}>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            className="font-body"
            style={{ color: COLORS.body }}
            data-testid="event-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="h-9 text-white font-body"
            style={{ backgroundColor: COLORS.primary }}
            data-testid="event-edit-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
